// ============================================================================
// Schema Zod — Validação de entrada para Importação de Clientes
// Baseado em: ClientSchema.js + dados reais do Firestore (19/07/2026)
//
// Este schema define o "contrato de entrada" para o JSON canônico.
// Diferente do ClientSchema.js (schema completo do Firestore), este schema
// valida APENAS os campos que o agente .md pode enviar.
// ============================================================================

import { z } from "zod";

// ── 0. UTILITÁRIOS DE VALIDAÇÃO ─────────────────────────────────────────────
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const CEP_REGEX = /^\d{5}-?\d{3}$/;

const dateValidation = (fieldName: string) =>
  z
    .string()
    .regex(DATE_REGEX, `${fieldName} deve estar no formato AAAA-MM-DD`)
    .optional()
    .or(z.literal(""));

const cepValidation = z
  .string()
  .regex(CEP_REGEX, "CEP deve estar no formato XXXXX-XXX ou XXXXXXXX")
  .max(9)
  .optional()
  .or(z.literal(""));

// ── 0. CAMPOS DE SISTEMA (Firestore — somente leitura na importação) ─────────
const SystemFieldsSchema = z.object({
  driveFolderId: z.string().optional(),
  driveTreeHealthy: z.boolean().optional(),
  auditLog: z
    .array(
      z.object({
        action: z.string().optional(),
        userId: z.string().optional(),
        timestamp: z.string().optional(),
        field: z.string().optional(),
        previousValue: z.any().optional(),
        newValue: z.any().optional(),
      }),
    )
    .optional(),
  id: z.string().optional(),
  searchKeywords: z.array(z.string()).optional(),
  qualificationText: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

// ── 1. TIPOS DE CLIENTE ──────────────────────────────────────────────────────
export const CLIENT_TYPE_VALUES = [
  "Pessoa Física",
  "Pessoa Jurídica",
  "Animal Não-Humano",
  "Menor/Incapaz",
  "Espólio (Falecido)",
  "Condomínio",
  "Pessoa Privada de Liberdade",
  "Nascituro (Gestação)",
  "Massa Falida",
] as const;

// ── 2. ENDEREÇO ──────────────────────────────────────────────────────────────
const AddressSchema = z.object({
  street: z.string().max(200).optional(),
  number: z.string().max(20).optional(),
  complement: z.string().max(100).optional(),
  neighborhood: z.string().max(100).optional(),
  city: z.string().max(100).optional(),
  state: z.string().length(2).optional(),
  zipCode: cepValidation,
});

// ── 3. CONTATO ───────────────────────────────────────────────────────────────
const ContactSchema = z.object({
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  phonePrimary: z
    .string()
    .min(10, "Telefone deve ter no mínimo 10 dígitos")
    .optional()
    .or(z.literal("")),
  emergencyContact: z
    .object({
      name: z.string().max(100).optional(),
      relation: z.string().max(50).optional(),
      phone: z.string().max(20).optional(),
    })
    .optional(),
});

// ── 4. REPRESENTANTE (âncora/tutor) ──────────────────────────────────────────
const RepresentativeSchema = z.object({
  id: z.string().optional(),
  name: z
    .string()
    .min(3, "Nome do representante deve ter no mínimo 3 caracteres")
    .optional(),
  cpf: z.string().min(11, "CPF do representante inválido").max(14).optional(),
  email: z.string().email().optional().or(z.literal("")),
  phone: z.string().optional(),
  nationality: z.string().optional(),
  civilStatus: z.string().optional(),
  profession: z.string().optional(),
  rg: z.string().optional(),
  address: AddressSchema.optional(),
  contact: z
    .object({
      phonePrimary: z.string().optional(),
      email: z.string().optional(),
    })
    .optional(),
});

// ── 5. REPRESENTANTE INTERNO (subobjeto dos tipos) ──────────────────────────
const InternalRepSchema = z.object({
  name: z.string().optional(),
  cpf: z.string().optional(),
  rg: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  nationality: z.string().optional(),
  civilStatus: z.string().optional(),
  profession: z.string().optional(),
  address: AddressSchema.optional(),
});

// ── 6. ELEITORAL (Pessoa Física) ─────────────────────────────────────────────
const ElectoralSchema = z.object({
  voterId: z.string().optional(),
  zone: z.string().optional(),
  section: z.string().optional(),
});

// ── 7. QUALIFICATION — PESSOA FÍSICA ─────────────────────────────────────────
const PhysicalSchema = z.object({
  name: z
    .string()
    .min(3, "Nome deve ter no mínimo 3 caracteres")
    .max(200, "Nome muito longo"),
  cpf: z
    .string()
    .min(11, "CPF deve ter 11 dígitos")
    .max(14, "CPF deve ter no máximo 14 caracteres")
    .regex(/^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/, "Formato de CPF inválido"),
  rg: z.string().max(20).optional().or(z.literal("")),
  orgaoEmissor: z.string().max(50).optional().or(z.literal("")),
  birthDate: dateValidation("Data de nascimento"),
  nationality: z.string().max(50).optional().default("brasileiro(a)"),
  naturalness: z.string().max(100).optional().or(z.literal("")),
  civilStatus: z.string().max(50).optional().or(z.literal("")),
  maritalRegime: z.string().max(50).optional().or(z.literal("")),
  spouseName: z.string().max(200).optional().or(z.literal("")),
  spouseCPF: z.string().max(14).optional().or(z.literal("")),
  profession: z.string().max(100).optional().or(z.literal("")),
  hasProfessionalRegister: z.boolean().optional(),
  registerType: z.string().max(50).optional().or(z.literal("")),
  registerUf: z.string().max(2).optional().or(z.literal("")),
  professionalRegister: z.string().max(30).optional().or(z.literal("")),
  socialName: z.string().max(200).optional().or(z.literal("")),
  motherName: z.string().max(200).optional().or(z.literal("")),
  fatherName: z.string().max(200).optional().or(z.literal("")),
  pronoun: z.string().max(20).optional().or(z.literal("")),
  genderIdentity: z.string().max(50).optional().or(z.literal("")),
  grammaticalGender: z.enum(["M", "F"]).optional(),
  electoral: ElectoralSchema.optional(),
  isPPE: z.boolean().optional(),
  ppeDescription: z.string().max(200).optional().or(z.literal("")),
});

// ── 8. QUALIFICATION — PESSOA JURÍDICA ──────────────────────────────────────
const LegalSchema = z.object({
  companyName: z
    .string()
    .min(3, "Nome da empresa deve ter no mínimo 3 caracteres"),
  tradeName: z.string().optional().or(z.literal("")),
  cnpj: z
    .string()
    .min(14, "CNPJ deve ter 14 dígitos")
    .max(18, "CNPJ muito longo"),
  stateRegistry: z.string().optional().or(z.literal("")),
  nire: z.string().optional().or(z.literal("")),
  constitutionDate: dateValidation("Data de constituição"),
  companyType: z.string().optional().or(z.literal("")),
  representativeRole: z.string().optional().default("Sócio-Administrador"),
  representative: InternalRepSchema.optional(),
});

// ── 9. QUALIFICATION — ANIMAL ────────────────────────────────────────────────
const AnimalSchema = z.object({
  name: z.string().min(2, "Nome do animal deve ter no mínimo 2 caracteres"),
  species: z.string().optional().default("Canina"),
  breed: z.string().optional().default("SRD"),
  scientificName: z.string().optional().or(z.literal("")),
  biologicalSex: z.string().optional().or(z.literal("")),
  reproductiveStatus: z.string().optional().or(z.literal("")),
  coat: z
    .object({
      color: z.string().optional(),
      pattern: z.string().optional(),
      texture: z.string().optional(),
    })
    .optional(),
  microchip: z.string().optional().or(z.literal("")),
  rga: z.string().optional().or(z.literal("")),
  distinctiveMarks: z.array(z.string()).optional(),
  estimatedAge: z.string().optional().or(z.literal("")),
  responsible: InternalRepSchema.optional(),
});

// ── 10. QUALIFICATION — DEMAIS TIPOS (simplificados) ────────────────────────
const MinorSchema = z.object({
  name: z.string().min(3),
  nationality: z.string().optional().default("brasileiro(a)"),
  rg: z.string().optional().or(z.literal("")),
  cpf: z.string().optional().or(z.literal("")),
  representativeType: z.string().optional().default("Assistido(a)"),
  guardianshipProcessNumber: z.string().optional().or(z.literal("")),
  representative: InternalRepSchema.optional(),
});

const ImprisonedSchema = z.object({
  name: z.string().min(3),
  rg: z.string().optional().or(z.literal("")),
  cpf: z.string().optional().or(z.literal("")),
  nationality: z.string().optional().default("brasileiro(a)"),
  civilStatus: z.string().optional(),
  profession: z.string().optional(),
  matriculaPrisional: z.string().optional().or(z.literal("")),
  prisonUnit: z.string().optional().or(z.literal("")),
  executionRegime: z.string().optional().or(z.literal("")),
  proxy: InternalRepSchema.optional(),
});

const EstateSchema = z.object({
  deceasedName: z.string().min(3),
  cpf: z.string().optional(),
  dateOfDeath: dateValidation("Data do óbito"),
  inventorier: InternalRepSchema.optional(),
});

const CondominiumSchema = z.object({
  name: z.string().min(3),
  cnpj: z.string().optional(),
  syndic: InternalRepSchema.optional(),
});

const UnbornSchema = z.object({
  mothersName: z.string().min(3),
  conceptionDate: dateValidation("Data de concepção"),
  expectedBirthDate: dateValidation("Data prevista de parto"),
  representative: InternalRepSchema.optional(),
});

const BankruptcySchema = z.object({
  companyName: z.string().min(3),
  cnpj: z.string().optional(),
  administrator: InternalRepSchema.optional(),
});

// ── 11. QUALIFICATION (união de todos os tipos) ──────────────────────────────
const QualificationSchema = z.object({
  physical: PhysicalSchema.optional(),
  legal: LegalSchema.optional(),
  animal: AnimalSchema.optional(),
  minor: MinorSchema.optional(),
  imprisoned: ImprisonedSchema.optional(),
  estate: EstateSchema.optional(),
  condominium: CondominiumSchema.optional(),
  unborn: UnbornSchema.optional(),
  bankruptcy: BankruptcySchema.optional(),
});

// ── 12. FINANCEIRO ───────────────────────────────────────────────────────────
const FinancialSchema = z.object({
  isProBono: z.boolean().optional(),
  hasLegalAid: z.boolean().optional(),
  bankAccount: z
    .object({
      bank: z.string().optional(),
      agency: z.string().optional(),
      account: z.string().optional(),
      pix: z.string().optional(),
    })
    .optional(),
});

// ── 13. SCHEMA PRINCIPAL — CLIENTE COMPLETO ──────────────────────────────────
export const ClientImportSchema = z
  .object({
    type: z.enum(CLIENT_TYPE_VALUES, {
      errorMap: () => ({
        message:
          "Tipo de cliente inválido. Valores aceitos: Pessoa Física, Pessoa Jurídica, Animal Não-Humano, Menor/Incapaz, Espólio (Falecido), Condomínio, Pessoa Privada de Liberdade, Nascituro (Gestação), Massa Falida",
      }),
    }),
    active: z.boolean().optional().default(true),
    grammaticalGender: z.enum(["M", "F"]).optional(),
    healthCondition: z.array(z.string()).optional(),
    roles: z
      .array(z.enum(["CLIENT", "PARTNER", "OTHER"]))
      .optional()
      .default(["CLIENT"]),
    isOfficeProfile: z.boolean().optional().default(false),
    clientOrigin: z.string().optional(),
    onboardingNotes: z
      .string()
      .max(2000, "Observações devem ter no máximo 2000 caracteres")
      .optional()
      .or(z.literal("")),
    onboardingStatus: z
      .enum(["APPROVED", "PENDING", "REJECTED"])
      .optional()
      .default("APPROVED"),
    isFullDependent: z.boolean().optional().default(false),
    livesWithAnchor: z.boolean().optional().default(false),
    anchor: RepresentativeSchema.nullable().optional(),
    qualification: QualificationSchema,
    contact: ContactSchema.optional(),
    location: z
      .object({
        status: z
          .enum([
            "Endereço Verificado",
            "Local Incerto e Não Sabido (LINS)",
            "Recolhido em Unidade Prisional",
            "Residente no Exterior",
          ])
          .optional()
          .default("Endereço Verificado"),
        address: AddressSchema.optional(),
      })
      .optional(),
    lgpdConsent: z
      .object({
        healthDataConsent: z.boolean().optional().default(false),
        consentDate: dateValidation("Data do consentimento LGPD"),
        consentDocUrl: z.string().optional(),
        consentTermVersion: z.string().optional().default("v1"),
      })
      .optional(),
    financial: FinancialSchema.optional(),
  })
  .and(SystemFieldsSchema)
  .superRefine((data, ctx) => {
    // Validar campos obrigatórios por tipo
    const type = data.type;
    const qual = data.qualification;

    if (type === "Pessoa Física" && !qual?.physical?.name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Nome é obrigatório para Pessoa Física",
        path: ["qualification", "physical", "name"],
      });
    }
    if (type === "Pessoa Física" && !qual?.physical?.cpf) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "CPF é obrigatório para Pessoa Física",
        path: ["qualification", "physical", "cpf"],
      });
    }
    if (type === "Pessoa Jurídica" && !qual?.legal?.companyName) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Nome da empresa é obrigatório para Pessoa Jurídica",
        path: ["qualification", "legal", "companyName"],
      });
    }
    if (type === "Pessoa Jurídica" && !qual?.legal?.cnpj) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "CNPJ é obrigatório para Pessoa Jurídica",
        path: ["qualification", "legal", "cnpj"],
      });
    }
    if (type === "Animal Não-Humano" && !qual?.animal?.name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Nome do animal é obrigatório",
        path: ["qualification", "animal", "name"],
      });
    }
    if (type === "Menor/Incapaz" && !qual?.minor?.name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Nome é obrigatório para Menor/Incapaz",
        path: ["qualification", "minor", "name"],
      });
    }
  });

// ── 14. TIPO INFERIDO ────────────────────────────────────────────────────────
export type ClientImportData = z.infer<typeof ClientImportSchema>;

// ── 15. SCHEMA MÍNIMO (apenas campos obrigatórios) ───────────────────────────
export const ClientImportMinSchema = z.object({
  type: z.enum(CLIENT_TYPE_VALUES),
  qualification: z.object({
    physical: z
      .object({
        name: z.string().min(3),
        cpf: z.string().min(11).max(14),
      })
      .optional(),
    legal: z
      .object({
        companyName: z.string().min(3),
        cnpj: z.string().min(14).max(18),
      })
      .optional(),
    animal: z
      .object({
        name: z.string().min(2),
      })
      .optional(),
    minor: z
      .object({
        name: z.string().min(3),
      })
      .optional(),
    estate: z
      .object({
        deceasedName: z.string().min(3),
      })
      .optional(),
    condominium: z
      .object({
        name: z.string().min(3),
      })
      .optional(),
    imprisoned: z
      .object({
        name: z.string().min(3),
      })
      .optional(),
    unborn: z
      .object({
        mothersName: z.string().min(3),
      })
      .optional(),
    bankruptcy: z
      .object({
        companyName: z.string().min(3),
      })
      .optional(),
  }),
});

export type ClientImportMinData = z.infer<typeof ClientImportMinSchema>;

// ── 16. FUNÇÃO DE VALIDAÇÃO ──────────────────────────────────────────────────
export function validateClientImport(data: unknown) {
  const result = ClientImportSchema.safeParse(data);
  if (!result.success) {
    return {
      valid: false as const,
      errors: result.error.issues.map((issue) => ({
        path: issue.path.join("."),
        message: issue.message,
      })),
    };
  }
  return { valid: true as const, data: result.data };
}
